import { trackEvent } from '../../../../utilities/use-analytics.js'
import { buildLocaleHref } from '../../../utils/locale-href.js'
import { useIntl } from '@cookbook/solid-intl'
import { messages } from './footer-organism.messages.en.js'

// Trailing slashes are normalised so '/fr' and '/fr/' compare equal.
const normalizePath = (path) => {
    const trimmed = path.replace(/\/+$/, '')
    return trimmed === '' ? '/' : trimmed
}

export function useFooterOrganismComponentController (props) {
    const intl = useIntl()
    const currentYear = new Date().getFullYear()
    const getFooterSections = () => [
        {
            links: [
                {
                    external: false,
                    href: '/',
                    name: 'home',
                    text: intl.formatMessage(messages.linkHome),
                },
                // { name: 'about-us', text: 'About Us', href: '/about-us', external: false },
                {
                    external: false,
                    href: '/newsroom',
                    name: 'lodgr-newsroom',
                    text: intl.formatMessage(messages.linkLodgrNewsroom),
                },
                {
                    external: false,
                    href: '/points',
                    name: 'points',
                    text: intl.formatMessage(messages.linkLodgrPoints),
                },

            ],
            title: intl.formatMessage(messages.sectionLodgr),
        },
        {
            links: [
                {
                    external: false,
                    href: '/faq/general',
                    name: 'general',
                    text: intl.formatMessage(messages.linkGeneralGettingStarted),
                },
                {
                    external: false,
                    href: '/faq/bookings',
                    name: 'bookings',
                    text: intl.formatMessage(messages.linkSearchingBookings),
                },
                {
                    external: false,
                    href: '/faq/pricing',
                    name: 'payments-and-fees',
                    text: intl.formatMessage(messages.linkPricingPayments),
                },
                {
                    external: false,
                    href: '/faq/hosting',
                    name: 'hosting',
                    text: intl.formatMessage(messages.linkHosting),
                },
                {
                    external: false,
                    href: '/faq/ical',
                    name: 'ical',
                    text: intl.formatMessage(messages.linkIcalCalendarSyncing),
                },

            ],
            title: intl.formatMessage(messages.sectionFaq),
        },
        {
            links: [
                {
                    external: false,
                    href: '/tos',
                    name: 'terms-and-conditions',
                    text: intl.formatMessage(messages.linkTermsConditions),
                },
                {
                    external: false,
                    href: '/privacy-policy',
                    name: 'privacy-policy',
                    text: intl.formatMessage(messages.linkPrivacyPolicy),
                },
                {
                    external: false,
                    href: '/contact',
                    name: 'contact',
                    text: intl.formatMessage(messages.linkContact),
                },
            ],
            title: `© ${currentYear}`,
        },
    ]

    const FOOTER_CLICK_PREFIX = 'clickCTAfromFooter_'

    // A footer link whose destination is the page already open is inert by default
    // (the browser has nowhere new to go), so the user's click appears to do nothing
    // — the TENANT-4572 report. JD's chosen behaviour is to scroll back to the top of
    // the current page instead. The locale prefix is applied to the target the same way
    // the rendered anchor's href is, so the comparison matches what the browser loads.
    const isCurrentPage = (link) => {
        if (link.external || typeof window === 'undefined') return false
        const targetPath = new URL(buildLocaleHref(link.href, intl.locale), window.location.origin).pathname
        return normalizePath(targetPath) === normalizePath(window.location.pathname)
    }

    const handleFooterClick = (event, link) => {
        if (isCurrentPage(link)) {
            event.preventDefault()
            window.scrollTo({
                behavior: 'smooth',
                top: 0,
            })
        }

        let eventName = ''

        switch (link.name) {
            case 'home': {
                eventName = 'Home';
                break;
            }
            case 'faq': {
                eventName = 'FAQ';
                break;
            }
            case 'general': {
                eventName = 'General';
                break;
            }
            case 'bookings': {
                eventName = 'Bookings';
                break;
            }
            case 'payments-and-fees': {
                eventName = 'Payments&Fees';
                break;
            }
            case 'hosting': {
                eventName = 'Hosting';
                break;
            }
            case 'ical': {
                eventName = 'iCal';
                break;
            }
            case 'lodgr-newsroom': {
                eventName = 'LodgrNewsroom';
                break;
            }
            case 'terms-and-conditions': {
                eventName = 'Terms&Conditions';
                break;
            }
            case 'privacy-policy': {
                eventName = 'PrivacyPolicy';
                break;
            }
            case 'contact': {
                eventName = 'Contact';
                break;
            }
            default: {
                // If no match, use the link name directly with first letter capitalized
                eventName = link.name.charAt(0).toUpperCase() + link.name.slice(1);
                break;
            }
        }

        trackEvent(FOOTER_CLICK_PREFIX + eventName);
    }

    /**
     * Scrawlr's corporate site, carried as an href rather than opened from a click handler.
     *
     * The CTA used to be a `<button onClick={() => window.open(...)}>`. That works in the SPA and
     * does nothing at all on the 96 prerendered location landing pages: their footer is
     * `renderToString`-ed and never hydrated — the landing island bundle mounts the hero search card
     * and the points-of-interest map, and claims nothing else — so the control shipped dead on every
     * one of them. As a link it needs no JavaScript, a crawler can follow it, and the SPA opens the
     * same URL in the same new tab. [doc: verifier finding 3]
     */
    const PLATFORM_LEARN_MORE_URL = 'https://corporate.scrawlr.com'

    const getPlatformSection = () => ({
        description: intl.formatMessage(messages.platformDescription),
        learnMoreHref: PLATFORM_LEARN_MORE_URL,
        logo: {
            alt: intl.formatMessage(messages.scrawlrLogoAlt),
            src: '/assets/logo/scrawlr-blue-wordmark.svg',
        },
    })

    return {
        footerSections: getFooterSections,
        handleFooterClick,
        platformSection: getPlatformSection,
    }
}
